import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  CCol, CRow, CButton, CAlert, CBadge,
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter, CSpinner,
  CCard, CCardHeader, CCardBody,
  CTable, CTableBody, CTableDataCell, CTableHead, CTableHeaderCell, CTableRow,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import {
  cilMap, cilArrowLeft, cilPen, cilTrash, cilWarning, cilCloudDownload, cilChartPie,
} from '@coreui/icons'
import { api, currentUser } from '../api/client'
import { fmtMoneda } from '../utils/moneda'
import { usePendiente } from '../components/ww/pendiente'

// Detalle de RUTA — réplica EN PANTALLA del
// reporte RptTotalRuta (arpttotalruta.cs:1404-1408): las 5 secciones del PDF
// (PRESTAMOS P009A2, ABONOS P009A3, ATRASOS P009A4, PRESTAMOS NUEVOS P009A5,
// GASTOS P009A6) con sus columnas GX, "NO HAY RECIBOS" en secciones vacías,
// los totales del pie (Total por Cobrar / Total Abonos / Total Prestó/Renovó /
// Total Gastos / Liquidar) y el botón "Descargar PDF" (GET reporte.pdf con
// token Bearer → blob → descarga RptTotalRuta.pdf).

// Moneda: fuente única utils/moneda.ts (fmtMoneda — símbolo configurado por
// empresa + máscara GX "$ Z,ZZZ,ZZZ,ZZ9.99", aticket.cs:222). [RGM] 2026-10-02 (F)

const fmtNumero = (v: any): string => {
  if (v === null || v === undefined || v === '') return '—'
  const n = Number(v)
  if (Number.isNaN(n)) return '—'
  return n.toLocaleString('en-US')
}

// Fechas GX "99/99/99" → dd/MM/yy (arpttotalruta.cs:242)
const fmtFecha = (v: any): string => {
  if (!v) return '—'
  const d = new Date(String(v).slice(0, 10))
  if (Number.isNaN(d.getTime())) return '—'
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yy = String(d.getFullYear()).slice(-2)
  return `${dd}/${mm}/${yy}`
}

const v = (valor: any): string => (valor === null || valor === undefined || valor === '' ? '—' : String(valor))

// Método de pago del abono (AbonoLibreValor3: 1=Efectivo, 2=Transferencia —
// semántica RGM; otro → vacío). Columna "Método" de la sección ABONOS. [RGM]
const metodoPagoTexto = (metodo: any): string =>
  Number(metodo) === 1 ? 'Efectivo' : Number(metodo) === 2 ? 'Transferencia' : ''

// Badge del porcentaje recaudado con color semáforo (diseño nuevo aprobado por Rafael):
// verde ≥70, naranja 40-69, rojo <40.
const badgeColor = (pct: number): 'success' | 'warning' | 'danger' =>
  pct >= 70 ? 'success' : pct >= 40 ? 'warning' : 'danger'

// ─── Tipado del reporte (contrato GET /rutas/{cod}/reporte) ─────────────────
interface ReportePrestamo {
  nro: number; posicionRuta: number; clienteNombre: string; direccion: string; celular: string
  negocio: string; reputacion: number; cantidadPrestamos: number
  fechaInicia: string; fechaTermina: string; valor: number; cuota: number; cuotas: number
  pagas: number; pendientes: number; fechaUltimoAbono: string; diasMora: number
  saldo: number; condenas: number; pico: number; atrasos: number
  observacion: string; observa: string; saldoReal: number; proximoCobro: string
  estadoCalc: string
}
interface ReporteAbono {
  nro: number; fecha: string; hora: string; valor: number; tipo: string; prestamoNro: number
  clienteNombre: string; posicionRuta: number; cuota: number; pagas: number; pendientes: number
  mora: number; saldo: number; condenas: number; proximoCobro: string; pico: number
  observacion: string; observa: string; reputacion: number; cantidadPrestamos: number
  fechaInicia: string; valorPrestamo: number; abonosEfectivo: number; cuotas: number; mPago: number
  // [RGM] Método de pago del abono (AbonoLibreValor3: 1=Efectivo, 2=Transferencia).
  metodoPago: number
}
interface ReporteAtrazo {
  nro: number; fecha: string; hora: string; valor: number; observacion: string
  prestamoNro: number; clienteNombre: string; cuota: number; pagas: number; pendientes: number
  mora: number; saldo: number; condenas: number; proximoCobro: string
}
interface ReportePrestamoNuevo {
  nro: number; fecha: string; valor: number; observacion: string; clienteNombre: string
  celular: string; fechaInicia: string; fechaTermina: string; tipo: string; negocio: string
  proximoCobro: string; valorCondena: number; atrazosCondenados: number; abonos: number; condenas: number
}
interface ReporteGasto {
  nro: number; fecha: string; hora: string; tipo: string; valor: number; observacion: string
}
interface ReporteTotales { porCobrar: number; abonos: number; prestamos: number; gastos: number; liquidar: number }
interface ReporteRuta {
  // [RGM] 2026-09-23 (pedido Rafael): Base de la ruta para el cierre.
  base: number
  zonaCodigo: number; zonaNombre: string; cobrador: string; fecha: string
  porcentajeRecaudados: number
  prestamos: ReportePrestamo[]; abonos: ReporteAbono[]; atrasos: ReporteAtrazo[]
  prestamosNuevos: ReportePrestamoNuevo[]; gastos: ReporteGasto[]
  totales: ReporteTotales
}

interface Columna {
  titulo: string
  texto: (fila: any) => string
  alinear?: 'end' | 'center'
}

// ─── Tabla compacta de una sección (réplica de columnas GX) ────────────────
const TablaSeccion: React.FC<{ columnas: Columna[]; filas: any[]; colorFila?: (fila: any) => string | undefined }> = ({
  columnas, filas, colorFila,
}) => (
  <div className="table-responsive" style={{ maxHeight: '24rem', overflowY: 'auto' }}>
    <CTable hover striped small align="middle" className="ww-table-embebida mb-0">
      <CTableHead>
        <CTableRow>
          {columnas.map((c) => (
            <CTableHeaderCell
              key={c.titulo}
              // bg-light FORZABA fondo claro en dark — el fondo lo da la clase
              // ww-table-embebida (--cui-table-bg, sigue el color-mode).
              className={`small text-body-secondary text-nowrap ${c.alinear === 'end' ? 'text-end' : c.alinear === 'center' ? 'text-center' : ''}`}
            >
              {c.titulo}
            </CTableHeaderCell>
          ))}
        </CTableRow>
      </CTableHead>
      <CTableBody>
        {filas.map((fila, i) => (
          <CTableRow key={i} style={colorFila ? { background: colorFila(fila) } : undefined}>
            {columnas.map((c) => (
              <CTableDataCell
                key={c.titulo}
                className={`small text-nowrap ${c.alinear === 'end' ? 'text-end' : c.alinear === 'center' ? 'text-center' : ''}`}
              >
                {c.texto(fila)}
              </CTableDataCell>
            ))}
          </CTableRow>
        ))}
        {filas.length === 0 && (
          <CTableRow>
            <CTableDataCell colSpan={columnas.length} className="text-center text-body-secondary py-3">
              {/* "NO HAY RECIBOS" (arpttotalruta.cs:341, 472, 574, 646, 696) */}
              NO HAY RECIBOS
            </CTableDataCell>
          </CTableRow>
        )}
      </CTableBody>
    </CTable>
  </div>
)

// ─── Card de sección con título + total al pie (textos GX) ─────────────────
const SeccionCard: React.FC<{
  titulo: string
  subtitulo?: string
  total?: { label: string; valor: number }
  children: React.ReactNode
}> = ({ titulo, subtitulo, total, children }) => (
  <CCard className="mb-3 shadow-sm" style={{ borderRadius: 8 }}>
    <CCardHeader className="d-flex align-items-center justify-content-between flex-wrap gap-2 fw-semibold border-bottom py-2">
      <span>{titulo}{subtitulo && <span className="text-body-secondary fw-normal ms-2">{subtitulo}</span>}</span>
    </CCardHeader>
    <CCardBody style={{ padding: '12px 16px' }}>
      {children}
      {total && (
        <div className="d-flex justify-content-end align-items-center gap-2 mt-2">
          <span className="fw-bold small">{total.label}</span>
          <span className="fw-bold small text-primary">{fmtNumero(total.valor)}</span>
        </div>
      )}
    </CCardBody>
  </CCard>
)

const RutaViewPage: React.FC = () => {
  const { cod } = useParams<{ cod: string }>()
  const navigate = useNavigate()
  const user = currentUser()
  const empresa = user?.companyId ?? '01'
  const [ruta, setRuta] = useState<any>(null)
  const [reporte, setReporte] = useState<ReporteRuta | null>(null)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [descargandoPdf, setDescargandoPdf] = useState(false)
  const [eliminarAbierto, setEliminarAbierto] = useState(false)
  const { aviso, setAviso } = usePendiente()

  // La ruta (nombre, cobrador) del listado
  useEffect(() => {
    setCargando(true)
    setError('')
    api.get<any[]>(`/rutas?empresa=${empresa}`)
      .then((lista) => {
        const encontrado = (lista ?? []).find((r) => String(r.zonaCodigo ?? '') === String(cod ?? ''))
        if (!encontrado) setError('Ruta no encontrada')
        setRuta(encontrado ?? null)
      })
      .catch((e: any) => setError(e.message ?? 'Ruta no encontrada'))
      .finally(() => setCargando(false))
  }, [cod, empresa])

  // El reporte completo del día (arpttotalruta.cs — réplica de los 5 cursores)
  const cobrador = ruta?.cobradorNombre || String(ruta?.cobradorCodigo ?? '') || '0'
  useEffect(() => {
    if (!cod) return
    setReporte(null)
    api.get<ReporteRuta>(`/rutas/${cod}/reporte?empresa=${empresa}&cobrador=${encodeURIComponent(cobrador)}`)
      .then(setReporte)
      .catch((e: any) => setError(e.message ?? 'Reporte no disponible'))
  }, [cod, empresa, cobrador])

  // PDF RptTotalRuta (el token JWT viaja en header Bearer, no en la URL).
  // [RGM] 2026-10-03 (pedido Rafael): abre en pestaña nueva vía api.abrirPdf
  // (antes hacía fetch manual + getToken + descarga con anchor).
  const descargarPdf = useCallback(async () => {
    if (!cod) return
    setDescargandoPdf(true)
    try {
      await api.abrirPdf(`/rutas/${cod}/reporte.pdf?empresa=${empresa}&cobrador=${encodeURIComponent(cobrador)}`)
    } catch (e: any) {
      setError(e.message ?? 'No se pudo abrir el PDF')
    } finally {
      setDescargandoPdf(false)
    }
  }, [cod, empresa, cobrador])

  const fila = (label: string, valor: any) => (
    <CCol md={6}>
      <div className="small text-body-secondary">{label}</div>
      <div className="fw-medium text-break">{valor ?? '—'}</div>
    </CCol>
  )

  const modificar = () => {
    setAviso('La modificación está pendiente de réplica (no existe endpoint de actualización en el backend)')
  }

  const confirmarEliminar = () => {
    setEliminarAbierto(false)
    setAviso('La eliminación está pendiente de réplica (no existe endpoint DELETE en el backend)')
  }

  // Columnas de cada sección (headers GX — arpttotalruta.cs:805-826, 833-858,
  // 865-880, 887-902, 908-916)
  const columnasPrestamos: Columna[] = [
    { titulo: 'Observa', texto: (f) => v(f.observa) },
    { titulo: 'Rep', texto: (f) => fmtNumero(f.reputacion), alinear: 'end' },
    { titulo: 'Ruta', texto: (f) => fmtNumero(f.posicionRuta), alinear: 'end' },
    { titulo: 'P.No', texto: (f) => fmtNumero(f.nro), alinear: 'end' },
    { titulo: 'Estado', texto: (f) => f.estadoCalc ?? '' },
    { titulo: 'Nombre', texto: (f) => v(f.clienteNombre) },
    { titulo: 'Inicia', texto: (f) => fmtFecha(f.fechaInicia), alinear: 'end' },
    { titulo: 'Termina', texto: (f) => fmtFecha(f.fechaTermina), alinear: 'end' },
    { titulo: 'Valor', texto: (f) => fmtMoneda(f.valor), alinear: 'end' },
    { titulo: 'Cuota', texto: (f) => fmtMoneda(f.cuota), alinear: 'end' },
    // [RGM] 2026-09-23 (pedido Rafael): Pagas en formato abonadas/total.
    { titulo: 'Pagas', texto: (f) => `${fmtNumero(f.pagas)}/${fmtNumero(f.cuotas)}`, alinear: 'end' },
    { titulo: 'Pend', texto: (f) => fmtNumero(f.pendientes), alinear: 'end' },
    { titulo: 'Mora', texto: (f) => fmtNumero(f.diasMora), alinear: 'end' },
    { titulo: 'Ultimo', texto: (f) => fmtFecha(f.fechaUltimoAbono), alinear: 'end' },
    { titulo: 'Cond', texto: (f) => fmtMoneda(f.condenas), alinear: 'end' },
    { titulo: 'Saldo', texto: (f) => fmtMoneda(f.saldo), alinear: 'end' },
    { titulo: 'Pico', texto: (f) => fmtMoneda(f.pico), alinear: 'end' },
    { titulo: 'Atr.', texto: (f) => fmtNumero(f.atrasos), alinear: 'end' },
    { titulo: 'Negocio', texto: (f) => v(f.negocio) },
    { titulo: 'Dirección', texto: (f) => v(f.direccion) },
    { titulo: 'Celular', texto: (f) => v(f.celular) },
    { titulo: 'Observación', texto: (f) => v(f.observacion) },
  ]

  const columnasAbonos: Columna[] = [
    { titulo: 'Observa', texto: (f) => v(f.observa) },
    { titulo: 'Rep', texto: (f) => fmtNumero(f.reputacion), alinear: 'end' },
    { titulo: 'Ruta', texto: (f) => fmtNumero(f.posicionRuta), alinear: 'end' },
    { titulo: 'Fecha', texto: (f) => fmtFecha(f.fecha), alinear: 'end' },
    { titulo: 'Hora', texto: (f) => v(f.hora) },
    { titulo: 'Nro.', texto: (f) => fmtNumero(f.prestamoNro), alinear: 'end' },
    { titulo: 'Valor', texto: (f) => fmtMoneda(f.valorPrestamo), alinear: 'end' },
    { titulo: 'Inicio', texto: (f) => fmtFecha(f.fechaInicia), alinear: 'end' },
    { titulo: 'V.Cuota', texto: (f) => fmtMoneda(f.cuota), alinear: 'end' },
    { titulo: 'V.Abono', texto: (f) => fmtMoneda(f.valor), alinear: 'end' },
    { titulo: 'Pico', texto: (f) => fmtMoneda(f.pico), alinear: 'end' },
    { titulo: 'Tipo', texto: (f) => v(f.tipo) },
    // [RGM] Método de pago del abono (1=Efectivo, 2=Transferencia; otro → '').
    { titulo: 'Método', texto: (f) => metodoPagoTexto(f.metodoPago) },
    { titulo: 'Pagas', texto: (f) => `${fmtNumero(f.pagas)}/${fmtNumero(f.cuotas)}`, alinear: 'end' },
    { titulo: 'Pend.', texto: (f) => fmtNumero(f.pendientes), alinear: 'end' },
    { titulo: 'Mora', texto: (f) => fmtNumero(f.mora), alinear: 'end' },
    { titulo: 'Saldo', texto: (f) => fmtMoneda(f.saldo), alinear: 'end' },
    { titulo: 'Cond', texto: (f) => fmtMoneda(f.condenas), alinear: 'end' },
    { titulo: 'Próximo', texto: (f) => fmtFecha(f.proximoCobro), alinear: 'end' },
    { titulo: 'Observación', texto: (f) => v(f.observacion) },
    { titulo: 'VerifiqueFecha', texto: () => '☐', alinear: 'center' },
  ]

  const columnasAtrasos: Columna[] = [
    { titulo: 'Observaciones', texto: (f) => v(f.observacion) },
    { titulo: 'Fecha', texto: (f) => fmtFecha(f.fecha), alinear: 'end' },
    { titulo: 'Hora', texto: (f) => v(f.hora) },
    { titulo: 'Nro.', texto: (f) => fmtNumero(f.nro), alinear: 'end' },
    { titulo: 'P.No', texto: (f) => fmtNumero(f.prestamoNro), alinear: 'end' },
    { titulo: 'Valor', texto: (f) => fmtMoneda(f.valor), alinear: 'end' },
    { titulo: 'Nombre', texto: (f) => v(f.clienteNombre) },
    { titulo: 'Pagas', texto: (f) => `${fmtNumero(f.pagas)}/${fmtNumero(f.pagas + f.pendientes)}`, alinear: 'end' },
    { titulo: 'Pend.', texto: (f) => fmtNumero(f.pendientes), alinear: 'end' },
    { titulo: 'Mora', texto: (f) => fmtNumero(f.mora), alinear: 'end' },
    { titulo: 'Saldo', texto: (f) => fmtMoneda(f.saldo), alinear: 'end' },
    { titulo: 'Condenas', texto: (f) => fmtMoneda(f.condenas), alinear: 'end' },
    { titulo: 'Proximo', texto: (f) => fmtFecha(f.proximoCobro), alinear: 'end' },
    { titulo: 'Verifique Fecha', texto: () => '☐', alinear: 'center' },
  ]

  const columnasNuevos: Columna[] = [
    { titulo: 'Fecha', texto: (f) => fmtFecha(f.fecha), alinear: 'end' },
    { titulo: 'Nro.', texto: (f) => fmtNumero(f.nro), alinear: 'end' },
    { titulo: 'Valor', texto: (f) => fmtMoneda(f.valor), alinear: 'end' },
    { titulo: 'Tipo', texto: (f) => v(f.tipo) },
    { titulo: 'Nombre', texto: (f) => v(f.clienteNombre) },
    { titulo: 'Negocio', texto: (f) => v(f.negocio) },
    { titulo: 'Celular', texto: (f) => v(f.celular) },
    { titulo: 'Inicia', texto: (f) => fmtFecha(f.fechaInicia), alinear: 'end' },
    { titulo: 'Termina', texto: (f) => fmtFecha(f.fechaTermina), alinear: 'end' },
    { titulo: 'Saldo', texto: (f) => fmtMoneda(f.condenas), alinear: 'end' },
    { titulo: 'Proximo', texto: (f) => fmtFecha(f.proximoCobro), alinear: 'end' },
    { titulo: 'Observación', texto: (f) => v(f.observacion) },
    { titulo: 'VerifiqueFecha', texto: () => '☐', alinear: 'center' },
  ]

  const columnasGastos: Columna[] = [
    { titulo: 'Nro.', texto: (f) => fmtNumero(f.nro), alinear: 'end' },
    { titulo: 'Fecha', texto: (f) => fmtFecha(f.fecha), alinear: 'end' },
    { titulo: 'Hora', texto: (f) => v(f.hora) },
    { titulo: 'Valor', texto: (f) => fmtMoneda(f.valor), alinear: 'end' },
    { titulo: 'Tipo', texto: (f) => v(f.tipo) },
    { titulo: 'Observación', texto: (f) => v(f.observacion) },
  ]

  if (cargando) {
    return (
      <div className="text-center py-5">
        <CSpinner color="primary" />
      </div>
    )
  }

  if (error && !ruta) {
    return (
      <>
        <CRow className="mb-4">
          <CCol className="d-flex align-items-center justify-content-between">
            <h4 className="ww-table-embebida mb-0">Ruta</h4>
            <CButton color="secondary" variant="outline" onClick={() => navigate('/rutas')}>
              <CIcon icon={cilArrowLeft} className="me-1" size="sm" />
              Volver
            </CButton>
          </CCol>
        </CRow>
        <CAlert color="warning">Ruta no encontrada</CAlert>
      </>
    )
  }

  const fechaReporte = reporte?.fecha ? new Date(String(reporte.fecha).slice(0, 10)) : null
  const hoy = fechaReporte ?? new Date()

  // Color de fila de PRESTAMOS (leyenda GX arpttotalruta.cs:794-795):
  // nuevo (Inicia ≥ hoy) verde, pasado (Termina < hoy) rojo.
  // Color de fila según el estado CALCULADO (reglas GX validadas):
  // Nuevo = verde (FechaInicia = hoy), Vencido = rojo (FechaTermina < hoy
  // Y SaldoReal > 0 — arptpasadosdefecha.cs:288), No Pagó = naranja.
  const colorFilaPrestamo = (fila: ReportePrestamo): string | undefined => {
    const h = hoy.getTime()
    const inicia = new Date(String(fila.fechaInicia).slice(0, 10)).getTime()
    const termina = new Date(String(fila.fechaTermina).slice(0, 10)).getTime()
    const proximo = new Date(String(fila.proximoCobro).slice(0, 10)).getTime()
    const ultimoAbono = new Date(String(fila.fechaUltimoAbono).slice(0, 10)).getTime()
    if (inicia === h) return 'var(--cui-success-bg-subtle, #d1e7dd)'
    if (proximo === h && ultimoAbono < h && fila.saldoReal > 0)
      return 'var(--cui-warning-bg-subtle, #fff3cd)'
    if (termina < h && fila.saldoReal > 0) return 'var(--cui-danger-bg-subtle, #f8d7da)'
    return undefined
  }

  return (
    <>
      <CRow className="mb-4">
        <CCol className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <h4 className="ww-table-embebida mb-0">Ruta {cod}{ruta?.zonaNombre ? ` — ${ruta.zonaNombre}` : ''}</h4>
            {reporte && (
              <CBadge color={badgeColor(reporte.porcentajeRecaudados)} className="fs-6">
                {reporte.porcentajeRecaudados}% recaudado
              </CBadge>
            )}
          </div>
          <div className="d-flex gap-2">
            <CButton color="secondary" variant="outline" onClick={() => navigate('/rutas')}>
              <CIcon icon={cilArrowLeft} className="me-1" size="sm" />
              Volver
            </CButton>
            <CButton color="primary" onClick={descargarPdf} disabled={descargandoPdf || !reporte}>
              <CIcon icon={cilCloudDownload} className="me-1" size="sm" />
              {descargandoPdf ? 'Generando...' : 'Descargar PDF'}
            </CButton>
            <CButton color="secondary" onClick={modificar}>
              <CIcon icon={cilPen} className="me-1" size="sm" />
              Modificar
            </CButton>
            <CButton color="danger" onClick={() => setEliminarAbierto(true)}>
              <CIcon icon={cilTrash} className="me-1" size="sm" />
              Eliminar
            </CButton>
          </div>
        </CCol>
      </CRow>

      {error && <CAlert color="danger">{error}</CAlert>}
      {aviso && <CAlert color="warning" dismissible onClose={() => setAviso('')}>{aviso}</CAlert>}

      <CCard className="mb-3 shadow-sm" style={{ borderRadius: 8 }}>
        <CCardHeader className="d-flex align-items-center gap-2 fw-semibold border-bottom py-2">
          <CIcon icon={cilMap} size="sm" /> Ruta
        </CCardHeader>
        <CCardBody style={{ padding: '14px 16px' }}>
          <CRow className="g-3">
            {fila('Zona', ruta?.zonaCodigo)}
            {fila('Nombre', ruta?.zonaNombre)}
            {fila('Caja', [ruta?.cajaNro, ruta?.cajaNombre].filter(Boolean).join(' · '))}
            {fila('Cobrador', [ruta?.cobradorCodigo, ruta?.cobradorNombre].filter(Boolean).join(' · '))}
            {fila('Supervisor', [ruta?.supervisorCodigo, ruta?.supervisorNombre].filter(Boolean).join(' · '))}
            {fila('Base', fmtMoneda(ruta?.zonaBase))}
          </CRow>
        </CCardBody>
      </CCard>

      {!reporte && !error && (
        <div className="text-center py-5">
          <CSpinner color="primary" />
        </div>
      )}

      {reporte && (
        <>
          <SeccionCard
            titulo="PRESTAMOS"
            subtitulo={`Porcentaje de Clientes Recaudados: ${reporte.porcentajeRecaudados}%`}
            total={{ label: 'Total por Cobrar: $', valor: reporte.totales.porCobrar }}
          >
            <TablaSeccion columnas={columnasPrestamos} filas={reporte.prestamos} colorFila={colorFilaPrestamo} />
          </SeccionCard>

          <SeccionCard
            titulo="ABONOS"
            subtitulo="MOVIMIENTOS DEL DIA COBRADOR"
            total={{ label: 'Total Abonos:', valor: reporte.totales.abonos }}
          >
            <TablaSeccion columnas={columnasAbonos} filas={reporte.abonos} />
          </SeccionCard>

          <SeccionCard titulo="ATRASOS">
            <TablaSeccion columnas={columnasAtrasos} filas={reporte.atrasos} />
          </SeccionCard>

          <SeccionCard
            titulo="PRESTAMOS NUEVOS"
            subtitulo="Prestó/Renovó"
            total={{ label: 'Total Prestó/Renovó:', valor: reporte.totales.prestamos }}
          >
            <TablaSeccion columnas={columnasNuevos} filas={reporte.prestamosNuevos} />
          </SeccionCard>

          <SeccionCard
            titulo="GASTOS"
            total={{ label: 'Total Gastos:', valor: reporte.totales.gastos }}
          >
            <TablaSeccion columnas={columnasGastos} filas={reporte.gastos} />
          </SeccionCard>

          {/* Caja "INFORMACIÓN CIERRE POR COBRADOR" + "Totales" (arpttotalruta.cs:710-726) */}
          <CCard className="mb-3 shadow-sm" style={{ borderRadius: 8 }}>
            <CCardHeader className="d-flex align-items-center justify-content-center gap-2 fw-semibold border-bottom py-2">
              <CIcon icon={cilChartPie} size="sm" /> INFORMACIÓN CIERRE POR COBRADOR
            </CCardHeader>
            <CCardBody style={{ padding: '14px 16px' }}>
              <div className="text-center fw-bold mb-3">Totales</div>
              <div className="row g-3">
                <div className="col">
                  <div className="small text-body-secondary">Total Abonos</div>
                  <div className="fw-bold">{fmtMoneda(reporte.totales.abonos)}</div>
                </div>
                <div className="col">
                  <div className="small text-body-secondary">Total Prestó/Renovó</div>
                  <div className="fw-bold">{fmtMoneda(reporte.totales.prestamos)}</div>
                </div>
                <div className="col">
                  <div className="small text-body-secondary">Total Gastos</div>
                  <div className="fw-bold">{fmtMoneda(reporte.totales.gastos)}</div>
                </div>
                {/* [RGM] 2026-09-23 (pedido Rafael): la Base de la ruta (que
                    está en la cabecera) se muestra también aquí — antes de
                    Liquidar, que queda AL FINAL. */}
                <div className="col">
                  <div className="small text-body-secondary">Base</div>
                  <div className="fw-bold">{fmtMoneda(reporte.base)}</div>
                </div>
                <div className="col">
                  <div className="small text-body-secondary">Liquidar</div>
                  <div className="fw-bold text-primary fs-5">{fmtMoneda(reporte.totales.liquidar)}</div>
                </div>
              </div>
            </CCardBody>
          </CCard>
        </>
      )}

      <CModal visible={eliminarAbierto} onClose={() => setEliminarAbierto(false)}>
        <CModalHeader>
          <CModalTitle>
            <CIcon icon={cilWarning} className="me-1 text-danger" />
            Eliminar ruta
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          <p className="ww-table-embebida mb-0">
            ¿Eliminar la ruta <strong>{cod}</strong> ({v(ruta?.zonaNombre)})?
          </p>
        </CModalBody>
        <CModalFooter className="border-top">
          <CButton color="secondary" type="button" onClick={() => setEliminarAbierto(false)}>Cancelar</CButton>
          <CButton color="danger" type="button" onClick={confirmarEliminar}>
            Eliminar
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default RutaViewPage
